import {
	AdAccountDTO,
	FacebookAdsCampaignDTO,
	GoogleAdsCampaignDTO
} from 'api-models'
import {
	ChangeEvent,
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toQuery } from 'utils'
import { useAssetStore } from '../../../../store'
import { useShallow } from 'zustand/react/shallow'
import { DateRangeType } from './types'

export type HandleSubItemChangeProps = {
	event: ChangeEvent<HTMLInputElement>
	key: string
	value: string | string[]
}

export type OptionSubContentProps = {
	handleSubItemChange: ({ event, key, value }: HandleSubItemChangeProps) => void
	mapState: Map<any, any>
}

export type FiltersType = {
	[key: string]: string | string[] | undefined
	ppc?: string
	status?: string[]
	converted?: string
	ip_address?: string
	placement?: string
	device_id?: string
	campaign_type?: string[]
	country?: string[]
	bounced?: string
	vpn?: string
	threat_level?: string[]
	bot_probability?: string[]
	user_interaction?: string[]
	ip_usage?: string
	identifier?: string
	session_id?: string
	labels?: string[]
	reason?: string[]
}

export type QuickViewType = {
	key: string
	label: string
}

interface ClicksPageFiltersContextType {
	filters: FiltersType
	quickView: QuickViewType
	mapState: Map<any, any>
	selectedAdAccount: AdAccountDTO | null
	setSelectedAdAccount: Dispatch<SetStateAction<AdAccountDTO | null>>
	selectedCampaign: GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null
	setSelectedCampaign: Dispatch<
		SetStateAction<GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null>
	>
	dateRange: { from?: Date; to?: Date } | null
	setDateRange: Dispatch<SetStateAction<{ from?: Date; to?: Date }>>
	handleApplyFilters: (newFilters: FiltersType) => void
	handleApplyDetailsItemFilter: (
		newFilters: FiltersType,
		adAccountId?: string,
		campaignId?: string
	) => void
	handleSubItemChange: ({ event, key, value }: HandleSubItemChangeProps) => void
	handleRemoveFilter: (filterKeyToRemove: string) => void
	handleOpenClickDetailsChange: (open: boolean) => void
	handleApplyQuickView: (newQuickView: QuickViewType) => void
	handleRemoveQuickView: () => void
}
export const ClicksPageFiltersContext = createContext(
	{} as ClicksPageFiltersContextType
)

const FILTER_KEYS_TYPE_ARRAY = [
	'status',
	'campaign_type',
	'country',
	'threat_level',
	'bot_probability',
	'user_interaction',
	'reason'
]

interface ClicksPageFiltersContextProviderProps {
	children: ReactNode
}
export function ClicksPageFiltersContextProvider({
	children
}: ClicksPageFiltersContextProviderProps) {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()

	const [filters, setFilters] = useState({} as FiltersType)
	const [quickView, setQuickView] = useState({} as QuickViewType)
	const { campaigns, adAccounts } = useAssetStore(
		useShallow((s) => ({
			campaigns: s.campaigns,
			adAccounts: s.adAccounts
		}))
	)
	const [mapState, setMapState] = useState(new Map())
	const [selectedAdAccount, setSelectedAdAccount] =
		useState<AdAccountDTO | null>(null)
	const [selectedCampaign, setSelectedCampaign] = useState<
		GoogleAdsCampaignDTO | FacebookAdsCampaignDTO | null
	>(null)
	const [dateRange, setDateRange] = useState<DateRangeType>({} as DateRangeType)

	const isClicksPage = window.location.pathname.includes('click')
	const hasInitializedFromURL = useRef(false)
	const lastSyncedAdAccountIdRef = useRef<string | null>(null)
	const isInitializingRef = useRef(false) 
	const urlSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null) 

	
	useEffect(() => {
		return () => {
			if (urlSyncTimeoutRef.current) {
				clearTimeout(urlSyncTimeoutRef.current)
				urlSyncTimeoutRef.current = null
			}
		}
	}, [])

	useEffect(() => {
		if (!isClicksPage) return
		if (adAccounts.length === 0 && campaigns.length === 0) return

		if (hasInitializedFromURL.current) {
			return
		}

		const hasPageFilterParams = Array.from(searchParams.keys()).some(
			(k) =>
				k !== 'from' && k !== 'to' &&
				k !== 'platforms' &&
				k !== 'adAccounts' && k !== 'adAccount' &&
				k !== 'breadCampaigns' && k !== 'breadCampaign' && k !== 'campaign'
		)

		if (!hasPageFilterParams) {
			let hasStoredPageFilters = false
			try {
				const stored = sessionStorage.getItem('clickguard_persisted_filters')
				if (stored) {
					const parsed = JSON.parse(stored)
					const clicksFilters = parsed.otherFiltersByPage?.clicks
					hasStoredPageFilters = clicksFilters && Object.keys(clicksFilters).length > 0
				}
			} catch {
				// ignore
			}
			if (hasStoredPageFilters) {
				return
			}
		}

		isInitializingRef.current = true

		const filters = {}
		const map = new Map()

		const reasonValues = searchParams.getAll('reason')
		let shouldUpdateParams = false
		const newParams = new URLSearchParams(searchParams)

		const adAccountParam = searchParams.get('adAccount') ||
			(searchParams.get('adAccounts')?.split(',')[0])
		if (adAccountParam) {
			const account = adAccounts.find((c) => c.id === adAccountParam)
			if (account) {
				setSelectedAdAccount(account)
				lastSyncedAdAccountIdRef.current = account.id
			}
		}

		const breadCampaigns = searchParams.get('breadCampaigns') || searchParams.get('breadCampaign')
		const campaignParam = searchParams.get('campaign') ||
			(breadCampaigns ? breadCampaigns.split(',')[0] : null)
		if (campaignParam) {
			const campaign = campaigns.find((c) => c.id === campaignParam)
			if (campaign) {
				setSelectedCampaign(campaign)
			}
		}

		searchParams.forEach((value, k) => {
			if (k === 'adAccount' || k === 'adAccounts' ||
				k === 'campaign' || k === 'breadCampaign' || k === 'breadCampaigns') {
				return
			}
			if (k === 'breadCampaign') {
				setSelectedCampaign(campaigns.find((c) => c.id === value) || null)
			}
			const key = k
			const formattedValue =
				key === 'reason'
					? reasonValues.length > 0
						? reasonValues
						: [value]
					: FILTER_KEYS_TYPE_ARRAY.includes(key)
						? value.split(',')
						: value

			Object.assign(filters, { [key]: formattedValue })
			map.set(key, formattedValue)
		})

		if (map.size === 0 && !searchParams.get('ppc')) {
			map.set('ppc', 'true')
			Object.assign(filters, {
				ppc: 'true'
			})
			newParams.set('ppc', 'true')
			shouldUpdateParams = true
		}

		setFilters(filters)
		setMapState(map)

		if (shouldUpdateParams) {
			setSearchParams(newParams, { replace: true })
		}

		hasInitializedFromURL.current = true
	}, [isClicksPage, adAccounts.length, campaigns.length, searchParams])

	useLayoutEffect(() => {
		if (isInitializingRef.current && hasInitializedFromURL.current && isClicksPage) {
			
			isInitializingRef.current = false
		}
	}, [selectedAdAccount, isClicksPage])

	
	// No URL write-back — breadcrumb owns the URL params

	const handleApplyQuickView = (newQuickView: QuickViewType) => {
		setQuickView(newQuickView)
	}
	const handleRemoveQuickView = () => {
		setQuickView({} as QuickViewType)
	}

	const handleApplyFilters = (newFilters: FiltersType) => {
		setFilters(newFilters)
		
		// Update mapState to keep it in sync with filters
		const newMap = new Map()
		Object.keys(newFilters).forEach((key) => {
			const value = newFilters[key]
			if (value) {
				newMap.set(key, value)
				// Reason strings can contain commas; use multiple params instead of join to avoid breaking on parse
				if (key === 'reason' && Array.isArray(value)) {
					searchParams.delete(key)
					value.forEach((r: string) => searchParams.append(key, r))
				} else {
					searchParams.set(key, Array.isArray(value) ? value.join(',') : value)
				}
			}
		})
		
		
		const filterKeys = Object.keys(newFilters)
		const navigationParams = ['adAccount', 'breadCampaign', 'ppc']
		searchParams.forEach((value, key) => {
			if (!filterKeys.includes(key) && !navigationParams.includes(key)) {
				const isFilterKey = FILTER_KEYS_TYPE_ARRAY.includes(key) || 
					['status', 'threat_level', 'bot_probability', 'ip_usage', 'device_type', 
					 'country', 'vpn', 'converted', 'bounced', 'campaign_type', 'type', 
					 'reason', 'ip_address', 'device_id', 'identifier', 'keyword', 
					 'placement', 'session_id', 'labels'].includes(key)
				if (isFilterKey) {
					searchParams.delete(key)
				}
			}
		})
		
		setMapState(newMap)
		setSearchParams(searchParams)
	}

	const handleApplyDetailsItemFilter = (
		newFilters: FiltersType,
		adAccountId?: string,
		campaignId?: string
	) => {
		if (!isClicksPage) {
			navigate(
				`../clicks?${toQuery(newFilters)}${
					adAccountId ? `&adAccount=${adAccountId}` : ''
				}${campaignId ? `&campaign=${campaignId}` : ''}`,
				{
					replace: true
				}
			)
			return
		}

		for (const key of searchParams.keys()) {
			searchParams.delete(key)
		}
		const map = new Map()
		Object.keys(newFilters).forEach((key) => {
			const value = newFilters[key] as string
			if (value) {
				map.set(key, value)
				searchParams.set(key, value)
			}
		})

		setSearchParams(searchParams)
		setMapState(map)
		setFilters(newFilters)
	}

	const handleSubItemChange = ({
		event,
		key,
		value
	}: HandleSubItemChangeProps) => {
		event.stopPropagation()
		if (value.length === 0) {
			handleRemoveFilter(key)
			return
		}
		const newMap = new Map(mapState)
		newMap.set(key, value)
		setMapState(newMap)
		
		const filters = mapFilters(newMap)
		handleApplyFilters(filters)
	}

	const handleRemoveFilter = (filterKeyToRemove: string) => {
		const copy = mapState
		copy.delete(filterKeyToRemove)
		setMapState(new Map(copy))

		const filters = mapFilters(copy)
		setFilters(filters)
		searchParams.delete(filterKeyToRemove)
		setSearchParams(searchParams)
	}

	const handleOpenClickDetailsChange = (open: boolean) => {
		if (!open) {
			const filters = mapFilters(mapState)
			handleApplyFilters(filters)
		}
	}

	const mapFilters = (map: Map<any, any>) => {
		const filters = {}
		map.forEach((value, key) => Object.assign(filters, { [key]: value }))
		return filters as FiltersType
	}

	return (
		<ClicksPageFiltersContext.Provider
			value={{
				filters,
				quickView,
				mapState,
				selectedAdAccount,
				setSelectedAdAccount,
				selectedCampaign,
				setSelectedCampaign,
				dateRange,
				setDateRange,
				handleApplyFilters,
				handleRemoveFilter,
				handleApplyDetailsItemFilter,
				handleOpenClickDetailsChange,
				handleSubItemChange,
				handleApplyQuickView,
				handleRemoveQuickView
			}}
		>
			{children}
		</ClicksPageFiltersContext.Provider>
	)
}
